import { ChainData, ChainType, Token } from "@0xsquid/squid-types";
import { coin as cosmJsCoin, OfflineSigner } from "@cosmjs/proto-signing";
import {
  type MsgSendEncodeObject,
  SigningStargateClient,
} from "@cosmjs/stargate";
import { Transaction as SuiTransaction } from "@mysten/sui/transactions";
import { Connection as SolanaConnection } from "@solana/web3.js";
import {
  BASE_FEE,
  nativeToScVal,
  Operation,
  Contract as StellarContract,
  Transaction as StellarTransaction,
  TransactionBuilder,
  type xdr,
} from "@stellar/stellar-sdk";
import {
  Contract as EvmContract,
  JsonRpcProvider,
  JsonRpcSigner,
} from "ethers";
import { erc20Abi } from "viem";
import { getClient } from "../../core/client";
import {
  CHAIN_IDS,
  nativeEvmTokenAddress,
  nativeStellarTokenAddress,
  nativeSuiTokenAddress,
  nativeXrplTokenAddress,
} from "../../core/constants";
import { BitcoinConnector } from "../../core/types/bitcoin";
import { SolanaConnector } from "../../core/types/solana";
import { StellarConnector } from "../../core/types/stellar";
import { SuiAccount, SuiConnector } from "../../core/types/sui";
import { XrplConnector, XrplTransactionType } from "../../core/types/xrpl";
import { scaleHbarToWei } from "./hederaService";
import { formatBNToReadable } from "./numberService";
import { executeSolanaTransfer } from "./solanaService";
import { getStellarNetwork, stellarAddressToScVal } from "./stellarService";
import { getSuiChain } from "./suiService";
import {
  getXrplNetwork,
  isXrplAddressValid,
  parseXrplTokenAddress,
} from "./xrplService";

interface SendTransactionParams {
  /**
   * The recipient address
   */
  to: string;
  /**
   * The amount of tokens to send, without decimals
   * e.g. 1000000 for 1 USDC
   */
  amount: string;
  token: Token;
}

export interface SendTransactionResponse {
  txHash: string;
}

interface SendTransactionEvmParams extends SendTransactionParams {
  signer: JsonRpcSigner;
  rpcUrl: string;
}

export async function sendTransactionEvm({
  to,
  amount,
  token,
  signer,
  rpcUrl,
}: SendTransactionEvmParams): Promise<SendTransactionResponse> {
  const isNativeToken =
    token.address.toLowerCase() === nativeEvmTokenAddress.toLowerCase();

  if (isNativeToken) {
    let value = amount;

    if (token.chainId === CHAIN_IDS.HEDERA) {
      value = scaleHbarToWei(BigInt(amount)).toString();
    }

    const tx = await signer.sendTransaction({
      to,
      value,
      chainId: Number(token.chainId),
    });

    return {
      txHash: tx.hash,
    };
  }

  const provider = new JsonRpcProvider(rpcUrl);
  const erc20Contract = new EvmContract(token.address, erc20Abi, provider);

  const encodedTransferCall = erc20Contract.interface.encodeFunctionData(
    "transfer",
    [to, amount]
  );

  const tx = await signer.sendTransaction({
    to: token.address,
    data: encodedTransferCall,
    chainId: Number(token.chainId),
  });

  return {
    txHash: tx.hash,
  };
}

interface SendTransactionCosmosParams extends SendTransactionParams {
  signer: OfflineSigner;
  rpcUrl: string;
  nativeToken: Token;
}

export async function sendTransactionCosmos({
  amount,
  rpcUrl,
  signer,
  to,
  token,
  nativeToken,
}: SendTransactionCosmosParams): Promise<SendTransactionResponse> {
  const accounts = await signer.getAccounts();
  const signingClient = await SigningStargateClient.connectWithSigner(
    rpcUrl,
    signer
  );

  const senderAddress = accounts[0].address;
  const coins = [cosmJsCoin(amount, token.address)];
  const fee = {
    amount: [cosmJsCoin("500", nativeToken.address)],
    gas: "150000",
  };

  const sendMsg: MsgSendEncodeObject = {
    typeUrl: "/cosmos.bank.v1beta1.MsgSend",
    value: {
      fromAddress: senderAddress,
      toAddress: to,
      amount: coins,
    },
  };

  const txHash = await signingClient.signAndBroadcastSync(
    senderAddress,
    [sendMsg],
    fee
  );

  return { txHash };
}

interface SendTxSolanaParams extends SendTransactionParams {
  signer: SolanaConnector;
  connection: SolanaConnection;
}

export async function sendTransactionSolana({
  signer,
  amount,
  to,
  token,
  connection,
}: SendTxSolanaParams): Promise<SendTransactionResponse> {
  const amountBn = BigInt(amount);

  const txHash = await executeSolanaTransfer({
    amount: amountBn,
    connection,
    signer,
    target: to,
    sourceToken: token,
  });

  return { txHash };
}

interface SendTxBitcoinParams extends SendTransactionParams {
  signer: BitcoinConnector;
}

export async function sendTransactionBitcoin({
  amount,
  signer,
  to,
}: SendTxBitcoinParams): Promise<SendTransactionResponse> {
  const { txHash } = await signer.sendBTC(to, Number(amount));

  return { txHash };
}

interface SendTxSuiParams extends SendTransactionParams {
  account: SuiAccount["account"];
  chain: ChainData;
  signer: SuiConnector;
}

export async function sendTransactionSui({
  account,
  amount,
  chain,
  signer,
  to,
  token,
}: SendTxSuiParams): Promise<SendTransactionResponse> {
  if (chain.chainType !== ChainType.SUI) {
    throw new Error(
      `Invalid chain type, expected ${ChainType.SUI}, got ${chain.chainType}`
    );
  }

  const suiChain = getSuiChain(chain.chainId);
  if (!suiChain) {
    throw new Error(`Sui chain not found for chainId: ${chain.chainId}`);
  }

  const suiClient = await getClient<ChainType.SUI>(chain);
  const isNativeToken =
    token.address.toLowerCase() === nativeSuiTokenAddress.toLowerCase();

  const tx = new SuiTransaction();

  if (isNativeToken) {
    const [coin] = tx.splitCoins(tx.gas, [amount]);

    tx.transferObjects([coin], to);
  } else {
    const coinType = token.address;

    const coinObjects = await suiClient.getCoins({
      owner: account.address,
      coinType,
    });

    const coinObject = coinObjects.data.find(
      (coin) => BigInt(coin.balance) >= BigInt(amount)
    );

    // if a coin with enough balance is found, use it
    // as it's more efficient than merging multiple coins
    if (coinObject) {
      const [coin] = tx.splitCoins(coinObject.coinObjectId, [amount]);

      tx.transferObjects([coin], to);
    } else {
      // otherwise merge coins up to the required amount

      // first, sort the coins by balance in descending order for efficiency
      const sortedCoins = coinObjects.data.sort((a, b) => {
        const diff = BigInt(b.balance) - BigInt(a.balance);

        if (diff === BigInt(0)) {
          return 0;
        } else if (diff > BigInt(0)) {
          return 1;
        } else {
          return -1;
        }
      });

      // gather coins until the required amount is reached
      const selectedCoinObjects = [];
      let amountGathered = BigInt(0);
      for (const coin of sortedCoins) {
        if (amountGathered >= BigInt(amount)) {
          break;
        }
        selectedCoinObjects.push(coin);
        amountGathered += BigInt(coin.balance);
      }

      if (amountGathered < BigInt(amount)) {
        throw new Error(`Insufficient balance of ${coinType}`);
      }

      const [targetCoin, ...otherCoins] = selectedCoinObjects.map(
        (c) => c.coinObjectId
      );

      // merge the selected coins into a single coin
      tx.mergeCoins(targetCoin, otherCoins);

      // create a new coin with the required amount
      const [coin] = tx.splitCoins(targetCoin, [amount]);

      tx.transferObjects([coin], to);
    }
  }

  const signedTx = await signer.signTransaction({
    transaction: tx,
    account,
    chain: suiChain,
  });

  // execute transaction without waiting for confirmation
  const txResponse = await suiClient.executeTransactionBlock({
    signature: signedTx.signature,
    transactionBlock: signedTx.bytes,
  });

  return { txHash: txResponse.digest };
}

interface SendTxXrplParams extends SendTransactionParams {
  signer: XrplConnector;
  from: string;
}

export async function sendTransactionXrpl({
  amount,
  signer,
  to,
  token,
  from,
}: SendTxXrplParams): Promise<SendTransactionResponse> {
  const xrplNetwork = getXrplNetwork(token.chainId);

  if (xrplNetwork == null) {
    throw new Error(`No XRPL network found for chainId '${token.chainId}'`);
  }

  if (!isXrplAddressValid(from) || !isXrplAddressValid(to)) {
    throw new Error("Invalid from or to address");
  }

  const baseTransaction = {
    TransactionType: XrplTransactionType.PAYMENT,
    Account: from,
    Destination: to,
  };

  const isNativeToken =
    token.address.toLowerCase() === nativeXrplTokenAddress.toLowerCase();

  if (isNativeToken) {
    const { hash } = await signer.signAndSubmit({
      network: xrplNetwork,
      tx: {
        ...baseTransaction,
        Amount: amount,
      },
    });

    return {
      txHash: hash,
    };
  }

  const asset = parseXrplTokenAddress(token.address);

  if (!asset) {
    throw new Error("Invalid asset");
  }

  const amountFormatted = formatBNToReadable(amount, token.decimals);

  const { hash } = await signer.signAndSubmit({
    network: xrplNetwork,
    tx: {
      ...baseTransaction,
      Amount: {
        currency: asset.code,
        issuer: asset.issuer,
        value: amountFormatted,
      },
    },
  });

  return {
    txHash: hash,
  };
}

interface SendTxStellarParams extends SendTransactionParams {
  signer: StellarConnector;
  chain: ChainData;
}

export async function sendTransactionStellar({
  amount,
  signer,
  to,
  token,
  chain,
}: SendTxStellarParams): Promise<SendTransactionResponse> {
  const network = getStellarNetwork(chain.chainId);

  if (!network) {
    throw new Error(`No Stellar network found for chainId ${chain.chainId}`);
  }

  const client = await getClient<ChainType.STELLAR>(chain);
  const { address } = await signer.getAddress();

  const isDestinationAccountActivated = await client.isAccountActivated(to);

  let operation: xdr.Operation<Operation>;

  // If account exists, call the "transfer" function on the token contract
  // This works for both native XLM and Contract Tokens since they share the same interface
  if (isDestinationAccountActivated) {
    // Create an instance of the token contract (Stellar Contract Token)
    // The interface is similar to ERC-20 ABI, having similar methods like transfer, approve, etc.
    // https://developers.stellar.org/docs/tokens/token-interface
    const contract = new StellarContract(token.address);

    // The "transfer" function is defined as:
    // transfer(from: Address, to: Address, amount: i128)
    // we need to convert JS values to Soroban Contract values (scVal)
    operation = contract.call(
      "transfer",
      stellarAddressToScVal(address),
      stellarAddressToScVal(to),
      nativeToScVal(amount, { type: "i128" })
    );
  } else {
    const isNativeToken =
      token.address.toLowerCase() === nativeStellarTokenAddress.toLowerCase();

    // If user is sending XLM, we can create the account
    if (isNativeToken) {
      operation = Operation.createAccount({
        destination: to,
        startingBalance: formatBNToReadable(amount, token.decimals),
      });
    } else {
      // If sending another token, throw error
      // Account needs to be created first
      throw new Error("Destination account is not activated");
    }
  }

  const sourceAccount = await client.getAccount(address);

  const builtTransaction = new TransactionBuilder(sourceAccount, {
    fee: (BigInt(BASE_FEE) * BigInt(2)).toString(),
    networkPassphrase: network,
  })
    .addOperation(operation)
    .setTimeout(300)
    .build();

  // Transactions invoking Soroban contracts (smart contract transactions)
  // must be simulated and populated before being signed and submitted.
  // https://developers.stellar.org/docs/learn/fundamentals/transactions/operations-and-transactions#transactions
  const txToSign = isDestinationAccountActivated
    ? // if account is activated, the transaction will be a contract invocation on "transfer" method
      await client.prepareTransaction(builtTransaction)
    : // If account is not activated, the transaction will be a CreateAccount operation
      builtTransaction;

  const { signedTxXdr } = await signer.signTransaction(txToSign.toXDR(), {
    networkPassphrase: network,
  });

  const signedTransaction = new StellarTransaction(signedTxXdr, network);

  const sentTransaction = await client.sendTransaction(signedTransaction);

  return {
    txHash: sentTransaction.hash,
  };
}
